import { z } from 'zod';
import {
  isFieldPathAllowed,
  isRelativeSemanticPath,
  isSafeFieldLabel,
  SemanticKind,
} from '../semantic/fieldPaths';

export const semanticEmbedderSettingsZod = z
  .object({
    provider: z.literal('azure_openai'),
    endpoint: z
      .string()
      .url()
      .refine(value => {
        try {
          const endpoint = new URL(value);
          return (
            endpoint.protocol === 'https:' &&
            !endpoint.username &&
            !endpoint.password &&
            !endpoint.search &&
            !endpoint.hash
          );
        } catch {
          return false;
        }
      }, 'Expected a credential-free HTTPS endpoint'),
    model: z.string().trim().min(1),
    apiVersion: z.string().trim().min(1),
  })
  .strict();

export const semanticCredentialZod = z
  .object({ apiKey: z.string().trim().min(1) })
  .strict();

export const embedderSaveZod = z
  .object({
    configuration: z
      .object({
        settings: semanticEmbedderSettingsZod,
        credential: z.discriminatedUnion('mode', [
          z
            .object({
              mode: z.literal('replace'),
              apiKey: z.string().trim().min(1),
            })
            .strict(),
          z.object({ mode: z.literal('retain') }).strict(),
        ]),
      })
      .strict(),
  })
  .strict();

export const storedSemanticEmbedderZod = z
  .object({
    schemaVersion: z.literal(1),
    settings: semanticEmbedderSettingsZod,
    encryptedCredential: z.string().min(1),
  })
  .strict();

export const semanticRecipeZod = z
  .object({
    id: z
      .string()
      .trim()
      .transform(value => value.normalize('NFC'))
      .refine(isSafeFieldLabel),
    fields: z
      .record(z.string().refine(isSafeFieldLabel), z.string())
      .default({}),
    collection: z.string().refine(isRelativeSemanticPath).optional(),
    identity: z.string().refine(isRelativeSemanticPath).optional(),
  })
  .strict()
  .refine(
    recipe => Boolean(recipe.collection) === Boolean(recipe.identity),
    'Collection and identity must be paired',
  );

export const semanticRecipeListZod = z
  .array(semanticRecipeZod)
  .refine(
    recipes =>
      new Set(recipes.map(recipe => recipe.id)).size === recipes.length,
    'Recipe IDs must be unique within a list',
  );

const recipesForKind = (kind: SemanticKind) =>
  semanticRecipeListZod.refine(
    recipes =>
      recipes.every(recipe =>
        Object.values(recipe.fields).every(path =>
          isFieldPathAllowed(path, kind, Boolean(recipe.collection)),
        ),
      ),
    'Invalid field scope or path',
  );

export const semanticContentZod = z
  .object({
    schemaVersion: z.literal(1),
    defaults: z
      .object({
        product: recipesForKind('product'),
        consumable: recipesForKind('consumable'),
      })
      .strict(),
    overrides: z
      .object({
        product: z.record(
          z.string().refine(isSafeFieldLabel),
          recipesForKind('product'),
        ),
        consumable: z.record(
          z.string().refine(isSafeFieldLabel),
          recipesForKind('consumable'),
        ),
      })
      .strict(),
  })
  .strict();

export type SemanticEmbedderSettings = z.infer<
  typeof semanticEmbedderSettingsZod
>;
export type SemanticCredential = z.infer<typeof semanticCredentialZod>;
export type SemanticEmbedderSave = z.infer<typeof embedderSaveZod>;
export type StoredSemanticEmbedder = z.infer<typeof storedSemanticEmbedderZod>;
export type SemanticRecipe = z.infer<typeof semanticRecipeZod>;
export type SemanticRecipeList = z.infer<typeof semanticRecipeListZod>;
export type SemanticContent = z.infer<typeof semanticContentZod>;
export type SemanticMigrationOutcome =
  | 'not_attempted'
  | 'migrated'
  | 'already_configured'
  | 'explicit_setup_required';

export interface SemanticEmbedderMetadata {
  configured: boolean;
  settings?: SemanticEmbedderSettings;
  credentialConfigured: boolean;
  migrationOutcome: SemanticMigrationOutcome;
}

export const defaultSemanticContent: SemanticContent = {
  schemaVersion: 1,
  defaults: {
    product: [
      {
        id: 'entity',
        fields: { name: 'self.name', description: 'self.description' },
      },
    ],
    consumable: [
      {
        id: 'entity',
        fields: { name: 'self.name', description: 'self.description' },
      },
    ],
  },
  overrides: { product: {}, consumable: {} },
};
